import { ArrowRightLeftIcon, LinkIcon, LockIcon, UserCheckIcon } from 'lucide-react'

import {
  SupportThreadDiagnostic,
  type SupportThreadDiagnosticProps,
} from '@/components/molecules/SupportThreadDiagnostic'
import { cn } from '@/lib/utils.ts'

interface SupportThreadMessageProps {
  readonly kind: 'message'
  readonly time: string
  readonly author: string
  readonly detail?: string
  readonly text: string
  readonly newLabel?: string
}

interface SupportThreadEventProps {
  readonly kind: 'event'
  readonly time: string
  readonly icon: 'taken' | 'status' | 'link'
  readonly text: string
  readonly link?: { readonly label: string; readonly href: string }
}

const EVENT_ICONS = { taken: UserCheckIcon, status: ArrowRightLeftIcon, link: LinkIcon } as const

const EventText = ({ text, link }: Pick<SupportThreadEventProps, 'text' | 'link'>) => {
  const at = link ? text.indexOf(link.label) : -1
  if (!link || at < 0) {
    return text
  }
  return (
    <>
      {text.slice(0, at)}
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">
        {link.label}
      </a>
      {text.slice(at + link.label.length)}
    </>
  )
}

interface SupportThreadNoteProps {
  readonly kind: 'note'
  readonly time: string
  readonly title: string
  readonly detail?: string
  readonly text: string
}

export type SupportThreadItemProps = (
  SupportThreadMessageProps | SupportThreadEventProps | SupportThreadNoteProps | SupportThreadDiagnosticProps
) & {
  readonly className?: string
}

export const SupportThreadItem = (props: SupportThreadItemProps) => {
  if (props.kind === 'diagnostic') {
    return <SupportThreadDiagnostic {...props} />
  }

  if (props.kind === 'event') {
    const Icon = EVENT_ICONS[props.icon]
    return (
      <div
        className={cn(
          'grid grid-cols-[72px_minmax(0,1fr)] gap-1 border-b border-gray-200 py-2 pr-6 text-sm text-blue-600',
          props.className
        )}>
        <span className="text-center">{props.time}</span>
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Icon aria-hidden className="size-[13px] text-blue-400" />
          <span>
            <EventText text={props.text} link={props.link} />
          </span>
        </span>
      </div>
    )
  }

  if (props.kind === 'note') {
    return (
      <article
        aria-label={props.title}
        className={cn(
          'grid grid-cols-[72px_minmax(0,1fr)] gap-1 border-b border-gray-200 bg-gray-100 py-4 pr-6',
          props.className
        )}>
        <span className="pt-0.5 text-center text-sm text-gray-900">{props.time}</span>
        <div className="flex min-w-0 flex-col gap-2 rounded-[4px] border border-dashed border-gray-600 bg-white px-3.5 py-3">
          <span className="inline-flex flex-wrap items-center gap-1.5 font-bold text-gray-900">
            <LockIcon aria-hidden className="size-3.5 text-gray-700" />
            {props.title}
            {props.detail && <span className="font-normal text-gray-600">· {props.detail}</span>}
          </span>
          <p className="text-base leading-[1.4] break-words whitespace-pre-wrap text-gray-800">{props.text}</p>
        </div>
      </article>
    )
  }

  return (
    <article
      className={cn(
        'grid grid-cols-[72px_minmax(0,1fr)] gap-1 border-b border-gray-200 py-5 pr-6',
        props.newLabel && 'bg-blue-100',
        props.className
      )}>
      <span className="pt-0.5 text-center text-sm text-gray-900">{props.time}</span>
      <div className="flex min-w-0 flex-col gap-2">
        <span className="font-bold text-gray-900">
          {props.author}
          {props.detail && <span className="font-normal text-gray-500"> · {props.detail}</span>}
          {props.newLabel && <span className="ml-2 font-bold text-blue-700">{props.newLabel}</span>}
        </span>
        <p className="text-lg leading-[1.4] break-words whitespace-pre-wrap text-gray-800">{props.text}</p>
      </div>
    </article>
  )
}
